/**
 * Chronotype regression over 90 days of sleep data.
 * Detects bedtime drift, midpoint shift, weekend social-jetlag.
 * Outputs a forward-looking projection: where your bedtime is heading.
 */

import type { SleepNight } from '../types.ts';

export interface ChronoRegression {
  midpoint_avg_h: number;
  midpoint_trend_min_per_week: number;
  midpoint_projected_30d_h: number;
  social_jetlag_h: number;
  consistency_z: number;
  classification: 'stable' | 'drifting_later' | 'drifting_earlier' | 'erratic';
}

export function regressChronotype(nights: readonly SleepNight[]): ChronoRegression {
  if (nights.length < 7) {
    return {
      midpoint_avg_h: 0,
      midpoint_trend_min_per_week: 0,
      midpoint_projected_30d_h: 0,
      social_jetlag_h: 0,
      consistency_z: 0,
      classification: 'erratic',
    };
  }
  const sorted = [...nights].sort((a, b) => a.date.localeCompare(b.date));
  const midpoints = sorted.map((n) => sleepMidpoint(n));
  const mean = midpoints.reduce((a, b) => a + b, 0) / midpoints.length;
  const variance = midpoints.reduce((a, b) => a + (b - mean) ** 2, 0) / midpoints.length;
  const std = Math.sqrt(variance);
  const xs = sorted.map((_, i) => i);
  const slope = linRegressSlope(xs, midpoints);
  const trend_min_per_week = slope * 7 * 60;
  const projected = mean + slope * 30;
  const weekendVsWeekday = socialJetlag(sorted);
  let classification: ChronoRegression['classification'] = 'stable';
  if (std > 1.2) classification = 'erratic';
  else if (Math.abs(trend_min_per_week) < 5) classification = 'stable';
  else if (trend_min_per_week > 5) classification = 'drifting_later';
  else classification = 'drifting_earlier';
  return {
    midpoint_avg_h: round1(mean),
    midpoint_trend_min_per_week: Math.round(trend_min_per_week),
    midpoint_projected_30d_h: round1(projected),
    social_jetlag_h: round1(weekendVsWeekday),
    consistency_z: round1(1 - Math.min(1, std / 2)),
    classification,
  };
}

function sleepMidpoint(n: SleepNight): number {
  const start = hourOfIso(n.sleep_start_iso);
  const end = hourOfIso(n.sleep_end_iso);
  const wrapped = end < start ? end + 24 : end;
  const mid = (start + wrapped) / 2;
  return mid > 24 ? mid - 24 : mid;
}

function hourOfIso(iso: string): number {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 0;
  return d.getUTCHours() + d.getUTCMinutes() / 60;
}

function linRegressSlope(xs: number[], ys: number[]): number {
  const n = xs.length;
  if (n === 0) return 0;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;
  for (let i = 0; i < n; i++) {
    const x = xs[i] ?? 0;
    const y = ys[i] ?? 0;
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumX2 += x * x;
  }
  const denom = n * sumX2 - sumX * sumX || 1;
  return (n * sumXY - sumX * sumY) / denom;
}

function socialJetlag(sorted: readonly SleepNight[]): number {
  const weekdayMids: number[] = [];
  const weekendMids: number[] = [];
  for (const n of sorted) {
    const d = new Date(n.date + 'T00:00:00Z');
    const dow = d.getUTCDay();
    const mid = sleepMidpoint(n);
    if (dow === 0 || dow === 6) weekendMids.push(mid);
    else weekdayMids.push(mid);
  }
  if (weekdayMids.length === 0 || weekendMids.length === 0) return 0;
  const wd = weekdayMids.reduce((a, b) => a + b, 0) / weekdayMids.length;
  const we = weekendMids.reduce((a, b) => a + b, 0) / weekendMids.length;
  return we - wd;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
